import React, { useState } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { formatCurrency } from '../utils/calculations';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import SEO from '../components/SEO';

ChartJS.register(ArcElement, Tooltip, Legend);

// Cost Inflation Index (CII) for indexation benefit
const CII_DATA = {
  '2001-02': 100,
  '2002-03': 105,
  '2003-04': 109,
  '2004-05': 113,
  '2005-06': 117,
  '2006-07': 122,
  '2007-08': 129,
  '2008-09': 137,
  '2009-10': 148,
  '2010-11': 167,
  '2011-12': 184,
  '2012-13': 200,
  '2013-14': 220,
  '2014-15': 240,
  '2015-16': 254,
  '2016-17': 264,
  '2017-18': 272,
  '2018-19': 280,
  '2019-20': 289,
  '2020-21': 301,
  '2021-22': 317,
  '2022-23': 331,
  '2023-24': 348,
  '2024-25': 363,
  '2025-26': 379,
};

const ASSET_TYPES = [
  { 
    id: 'equity_shares', 
    name: 'Listed Equity Shares', 
    ltcgPeriod: 12, 
    stcgRate: 20, 
    ltcgRate: 12.5, 
    ltcgExemption: 125000,
    indexation: false,
    description: 'STT paid listed shares on recognized stock exchange'
  },
  { 
    id: 'equity_mf', 
    name: 'Equity Mutual Funds', 
    ltcgPeriod: 12, 
    stcgRate: 20, 
    ltcgRate: 12.5, 
    ltcgExemption: 125000,
    indexation: false,
    description: 'Equity-oriented MF (>65% in equity)'
  },
  { 
    id: 'debt_mf', 
    name: 'Debt Mutual Funds', 
    ltcgPeriod: 36, 
    stcgRate: 'slab', 
    ltcgRate: 'slab', 
    ltcgExemption: 0,
    indexation: false,
    description: 'Taxed at slab rate (post April 2023)'
  },
  { 
    id: 'real_estate', 
    name: 'Real Estate / Property', 
    ltcgPeriod: 24, 
    stcgRate: 'slab', 
    ltcgRate: 12.5, 
    ltcgExemption: 0,
    indexation: false,
    description: 'Land, building, house property (Budget 2024: No indexation)'
  },
  { 
    id: 'gold_physical', 
    name: 'Physical Gold / Jewellery', 
    ltcgPeriod: 24, 
    stcgRate: 'slab', 
    ltcgRate: 12.5, 
    ltcgExemption: 0,
    indexation: false,
    description: 'Gold bars, coins, jewellery'
  },
  { 
    id: 'gold_etf', 
    name: 'Gold ETF / Gold MF', 
    ltcgPeriod: 12, 
    stcgRate: 20, 
    ltcgRate: 12.5, 
    ltcgExemption: 125000,
    indexation: false,
    description: 'Gold ETFs and Gold Mutual Funds (Budget 2024)'
  },
  { 
    id: 'unlisted_shares', 
    name: 'Unlisted Shares', 
    ltcgPeriod: 24, 
    stcgRate: 'slab', 
    ltcgRate: 12.5, 
    ltcgExemption: 0,
    indexation: false,
    description: 'Shares of unlisted companies'
  },
  { 
    id: 'bonds', 
    name: 'Bonds / Debentures', 
    ltcgPeriod: 12, 
    stcgRate: 'slab', 
    ltcgRate: 12.5, 
    ltcgExemption: 0,
    indexation: false,
    description: 'Listed bonds and debentures'
  },
];

const CapitalGainCalculator = () => {
  const [assetType, setAssetType] = useState('equity_shares');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [salePrice, setSalePrice] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [saleDate, setSaleDate] = useState('');
  const [expenses, setExpenses] = useState('0');
  const [taxSlab, setTaxSlab] = useState('30');
  const [result, setResult] = useState(null);

  const selectedAsset = ASSET_TYPES.find(a => a.id === assetType);

  const getFinancialYear = (date) => {
    const d = new Date(date);
    const month = d.getMonth();
    const year = d.getFullYear();
    if (month >= 3) {
      return `${year}-${(year + 1).toString().slice(2)}`;
    }
    return `${year - 1}-${year.toString().slice(2)}`;
  };

  const calculateHoldingPeriod = () => {
    if (!purchaseDate || !saleDate) return 0;
    const purchase = new Date(purchaseDate);
    const sale = new Date(saleDate);
    const diffTime = sale - purchase;
    const diffMonths = diffTime / (1000 * 60 * 60 * 24 * 30.44);
    return Math.floor(diffMonths);
  };

  const calculate = () => {
    const purchase = parseFloat(purchasePrice) || 0;
    const sale = parseFloat(salePrice) || 0;
    const qty = parseFloat(quantity) || 1;
    const exp = parseFloat(expenses) || 0;
    const slabRate = parseFloat(taxSlab) || 30;

    if (purchase <= 0 || sale <= 0 || !purchaseDate || !saleDate) {
      alert('Please fill all required fields with valid values');
      return;
    }

    const holdingMonths = calculateHoldingPeriod();
    const isLongTerm = holdingMonths >= selectedAsset.ltcgPeriod;

    const totalPurchase = purchase * qty;
    const totalSale = sale * qty;
    const totalExpenses = exp;

    let indexedCost = totalPurchase;
    let indexationBenefit = 0;

    // Calculate indexation if applicable (for assets that still allow it)
    if (isLongTerm && selectedAsset.indexation) {
      const purchaseFY = getFinancialYear(purchaseDate);
      const saleFY = getFinancialYear(saleDate);
      const purchaseCII = CII_DATA[purchaseFY] || 100;
      const saleCII = CII_DATA[saleFY] || 363;
      indexedCost = (totalPurchase * saleCII) / purchaseCII;
      indexationBenefit = indexedCost - totalPurchase;
    }

    const costOfAcquisition = selectedAsset.indexation && isLongTerm ? indexedCost : totalPurchase;
    const capitalGain = totalSale - costOfAcquisition - totalExpenses;
    const isProfit = capitalGain > 0;

    let taxableGain = capitalGain;
    let exemptionUsed = 0;
    let taxRate = 0;
    let taxAmount = 0;
    let surcharge = 0;
    let cess = 0;
    let totalTax = 0;

    if (isProfit) {
      if (isLongTerm) {
        // Long-term capital gain
        if (selectedAsset.ltcgExemption > 0) {
          exemptionUsed = Math.min(capitalGain, selectedAsset.ltcgExemption);
          taxableGain = Math.max(0, capitalGain - exemptionUsed);
        }
        
        if (selectedAsset.ltcgRate === 'slab') {
          taxRate = slabRate;
        } else {
          taxRate = selectedAsset.ltcgRate;
        }
      } else {
        // Short-term capital gain
        if (selectedAsset.stcgRate === 'slab') {
          taxRate = slabRate;
        } else {
          taxRate = selectedAsset.stcgRate;
        }
      }

      taxAmount = (taxableGain * taxRate) / 100;

      // Surcharge calculation (simplified)
      if (taxableGain > 5000000) {
        if (taxableGain <= 10000000) {
          surcharge = taxAmount * 0.10;
        } else if (taxableGain <= 20000000) {
          surcharge = taxAmount * 0.15;
        } else if (taxableGain <= 50000000) {
          surcharge = taxAmount * 0.25;
        } else {
          surcharge = taxAmount * 0.37;
        }
        // Cap surcharge at 15% for LTCG on equity
        if (isLongTerm && (assetType === 'equity_shares' || assetType === 'equity_mf')) {
          surcharge = Math.min(surcharge, taxAmount * 0.15);
        }
      }

      // Health & Education Cess @ 4%
      cess = (taxAmount + surcharge) * 0.04;
      totalTax = taxAmount + surcharge + cess;
    }

    const netProfit = capitalGain - totalTax;

    setResult({
      assetName: selectedAsset.name,
      holdingMonths,
      holdingYears: (holdingMonths / 12).toFixed(1),
      isLongTerm,
      gainType: isLongTerm ? 'Long-Term Capital Gain (LTCG)' : 'Short-Term Capital Gain (STCG)',
      totalPurchase,
      totalSale,
      totalExpenses,
      indexedCost,
      indexationBenefit,
      capitalGain,
      isProfit,
      exemptionUsed,
      taxableGain,
      taxRate,
      taxAmount,
      surcharge,
      cess,
      totalTax,
      netProfit,
      effectiveTaxRate: capitalGain > 0 ? ((totalTax / capitalGain) * 100).toFixed(2) : 0,
    });
  };

  const formatCurrencyForPDF = (amount) => {
    return 'Rs. ' + Math.round(amount).toLocaleString('en-IN');
  };

  const downloadPDF = () => {
    if (!result) return;

    const doc = new jsPDF();
    
    doc.setFontSize(18);
    doc.setTextColor(40, 40, 40);
    doc.text('Capital Gain Tax Calculator', 105, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated on: ${new Date().toLocaleDateString('en-IN')}`, 105, 28, { align: 'center' });

    // Asset Details
    doc.setFontSize(12);
    doc.setTextColor(40);
    doc.text('Asset Details', 14, 40);

    autoTable(doc, {
      startY: 45,
      head: [['Parameter', 'Value']],
      body: [
        ['Asset Type', result.assetName],
        ['Purchase Date', new Date(purchaseDate).toLocaleDateString('en-IN')],
        ['Sale Date', new Date(saleDate).toLocaleDateString('en-IN')],
        ['Holding Period', `${result.holdingMonths} months (${result.holdingYears} years)`],
        ['Capital Gain Type', result.gainType],
      ],
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] },
    });

    // Financial Summary
    let finalY = doc.lastAutoTable.finalY + 10;
    doc.text('Financial Summary', 14, finalY);

    autoTable(doc, {
      startY: finalY + 5,
      head: [['Description', 'Amount (Rs.)']],
      body: [
        ['Total Purchase Value', formatCurrencyForPDF(result.totalPurchase)],
        ['Total Sale Value', formatCurrencyForPDF(result.totalSale)],
        ['Expenses (Brokerage, etc.)', formatCurrencyForPDF(result.totalExpenses)],
        ...(result.indexationBenefit > 0 ? [['Indexation Benefit', formatCurrencyForPDF(result.indexationBenefit)]] : []),
        ['Capital Gain / (Loss)', formatCurrencyForPDF(result.capitalGain)],
      ],
      theme: 'striped',
      headStyles: { fillColor: [16, 185, 129] },
    });

    // Tax Calculation
    if (result.isProfit) {
      finalY = doc.lastAutoTable.finalY + 10;
      doc.text('Tax Calculation', 14, finalY);

      autoTable(doc, {
        startY: finalY + 5,
        head: [['Description', 'Amount (Rs.)']],
        body: [
          ['Gross Capital Gain', formatCurrencyForPDF(result.capitalGain)],
          ...(result.exemptionUsed > 0 ? [['Less: Exemption (Section 112A)', formatCurrencyForPDF(result.exemptionUsed)]] : []),
          ['Taxable Capital Gain', formatCurrencyForPDF(result.taxableGain)],
          [`Tax @ ${result.taxRate}%`, formatCurrencyForPDF(result.taxAmount)],
          ['Surcharge', formatCurrencyForPDF(result.surcharge)],
          ['Health & Education Cess @ 4%', formatCurrencyForPDF(result.cess)],
          ['Total Tax Liability', formatCurrencyForPDF(result.totalTax)],
          ['Net Profit After Tax', formatCurrencyForPDF(result.netProfit)],
        ],
        theme: 'striped',
        headStyles: { fillColor: [239, 68, 68] },
      });
    }

    // Disclaimer
    finalY = doc.lastAutoTable.finalY + 15;
    doc.setFontSize(8);
    doc.setTextColor(128);
    doc.text('Disclaimer: This is an estimate based on Budget 2024 tax rates. Consult a CA for accurate tax computation.', 14, finalY);

    doc.save('Capital_Gain_Tax_Report.pdf');
  };

  const chartData = result && result.isProfit ? {
    labels: ['Net Profit', 'Total Tax', 'Exemption Used'],
    datasets: [{
      data: [
        Math.max(0, result.netProfit),
        result.totalTax,
        result.exemptionUsed
      ],
      backgroundColor: ['#10b981', '#ef4444', '#f59e0b'],
      borderWidth: 0,
    }]
  } : null;

  return (
    <div className="calculator-page">
      <SEO 
        title="Capital Gain Tax Calculator"
        description="Free Capital Gain Tax Calculator India - Calculate STCG & LTCG tax on stocks, mutual funds, property."
        keywords="capital gain tax calculator, LTCG calculator, STCG calculator, capital gain calculator India, stock capital gain tax, property capital gain tax"
        canonicalPath="/capital-gain"
      />
      <div className="calculator-header">
        <h1>📈 Capital Gain Tax Calculator</h1>
        <p>Calculate capital gains tax on stocks, mutual funds, real estate, and gold as per Budget 2024 rules.</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Asset Type</label>
            <select value={assetType} onChange={(e) => setAssetType(e.target.value)}>
              {ASSET_TYPES.map(asset => (
                <option key={asset.id} value={asset.id}>{asset.name}</option>
              ))}
            </select>
            <small style={{ color: '#64748b', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              {selectedAsset?.description}
            </small>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="input-group">
              <label>Purchase Date</label>
              <input 
                type="date" 
                value={purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
              />
            </div>
            <div className="input-group">
              <label>Sale Date</label>
              <input 
                type="date" 
                value={saleDate}
                onChange={(e) => setSaleDate(e.target.value)}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="input-group">
              <label>Purchase Price (per unit) ₹</label>
              <input 
                type="number" 
                value={purchasePrice}
                onChange={(e) => setPurchasePrice(e.target.value)}
                placeholder="e.g., 1000"
              />
            </div>
            <div className="input-group">
              <label>Sale Price (per unit) ₹</label>
              <input 
                type="number" 
                value={salePrice}
                onChange={(e) => setSalePrice(e.target.value)}
                placeholder="e.g., 1500"
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="input-group">
              <label>Quantity / Units</label>
              <input 
                type="number" 
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="e.g., 100"
              />
            </div>
            <div className="input-group">
              <label>Expenses (Brokerage, etc.) ₹</label>
              <input 
                type="number" 
                value={expenses}
                onChange={(e) => setExpenses(e.target.value)}
                placeholder="e.g., 500"
              />
            </div>
          </div>

          {(selectedAsset?.stcgRate === 'slab' || selectedAsset?.ltcgRate === 'slab') && (
            <div className="input-group">
              <label>Your Income Tax Slab Rate (%)</label>
              <select value={taxSlab} onChange={(e) => setTaxSlab(e.target.value)}>
                <option value="0">0% (No Tax)</option>
                <option value="5">5%</option>
                <option value="10">10%</option>
                <option value="15">15%</option>
                <option value="20">20%</option>
                <option value="30">30%</option>
              </select>
              <small style={{ color: '#64748b', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                Select your applicable income tax slab for assets taxed at slab rate
              </small>
            </div>
          )}

          <button className="btn btn-primary" style={{ width: '100%', marginTop: '10px' }} onClick={calculate}>
            Calculate Capital Gain Tax
          </button>
        </div>

        <div className="calculator-results">
          {result ? (
            <>
              <div className="result-summary" style={{ 
                background: result.capitalGain >= 0 
                  ? 'linear-gradient(135deg, #10b981, #059669)' 
                  : 'linear-gradient(135deg, #ef4444, #dc2626)'
              }}>
                <h3>{result.gainType}</h3>
                <div className="amount">
                  {result.capitalGain >= 0 ? '+' : ''}{formatCurrency(result.capitalGain)}
                </div>
                <p style={{ opacity: 0.9, marginTop: '8px' }}>
                  Holding: {result.holdingMonths} months ({result.holdingYears} years)
                </p>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Total Purchase Value</span>
                  <span className="value">{formatCurrency(result.totalPurchase)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Total Sale Value</span>
                  <span className="value">{formatCurrency(result.totalSale)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Expenses</span>
                  <span className="value">{formatCurrency(result.totalExpenses)}</span>
                </div>
                {result.indexationBenefit > 0 && (
                  <div className="result-item" style={{ background: '#ecfdf5' }}>
                    <span className="label">Indexation Benefit</span>
                    <span className="value" style={{ color: '#10b981' }}>+{formatCurrency(result.indexationBenefit)}</span>
                  </div>
                )}
              </div>

              {result.isProfit && (
                <div className="result-details" style={{ marginTop: '16px' }}>
                  <h4 style={{ marginBottom: '12px', color: '#1e293b' }}>Tax Calculation</h4>
                  {result.exemptionUsed > 0 && (
                    <div className="result-item" style={{ background: '#fef3c7' }}>
                      <span className="label">Exemption u/s 112A (up to ₹1.25L)</span>
                      <span className="value" style={{ color: '#f59e0b' }}>-{formatCurrency(result.exemptionUsed)}</span>
                    </div>
                  )}
                  <div className="result-item">
                    <span className="label">Taxable Gain</span>
                    <span className="value">{formatCurrency(result.taxableGain)}</span>
                  </div>
                  <div className="result-item">
                    <span className="label">Tax @ {result.taxRate}%</span>
                    <span className="value">{formatCurrency(result.taxAmount)}</span>
                  </div>
                  {result.surcharge > 0 && (
                    <div className="result-item">
                      <span className="label">Surcharge</span>
                      <span className="value">{formatCurrency(result.surcharge)}</span>
                    </div>
                  )}
                  <div className="result-item">
                    <span className="label">Cess @ 4%</span>
                    <span className="value">{formatCurrency(result.cess)}</span>
                  </div>
                  <div className="result-item" style={{ background: '#fef2f2' }}>
                    <span className="label" style={{ fontWeight: '600' }}>Total Tax Liability</span>
                    <span className="value" style={{ color: '#ef4444', fontWeight: '600' }}>{formatCurrency(result.totalTax)}</span>
                  </div>
                  <div className="result-item" style={{ background: '#ecfdf5' }}>
                    <span className="label" style={{ fontWeight: '600' }}>Net Profit After Tax</span>
                    <span className="value" style={{ color: '#10b981', fontWeight: '600' }}>{formatCurrency(result.netProfit)}</span>
                  </div>
                  <div className="result-item">
                    <span className="label">Effective Tax Rate</span>
                    <span className="value">{result.effectiveTaxRate}%</span>
                  </div>
                </div>
              )}

              {chartData && result.capitalGain > 0 && (
                <div className="chart-container">
                  <h4 style={{ marginBottom: '12px' }}>Profit Distribution</h4>
                  <Pie data={chartData} options={{ 
                    responsive: true,
                    plugins: { legend: { position: 'bottom' } }
                  }} />
                </div>
              )}

              <div className="button-group" style={{ marginTop: '20px' }}>
                <button className="btn btn-secondary" onClick={downloadPDF} style={{ flex: 1 }}>
                  Download PDF Report
                </button>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
              <p>Enter your transaction details and click "Calculate" to see the capital gain tax.</p>
            </div>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>📌 Budget 2024 Capital Gain Tax Rules</h4>
        <div style={{ overflowX: 'auto', marginTop: '12px' }}>
          <table className="schedule-table">
            <thead>
              <tr>
                <th>Asset Type</th>
                <th>LTCG Period</th>
                <th>STCG Rate</th>
                <th>LTCG Rate</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Listed Equity / Equity MF</td>
                <td>12 months</td>
                <td>20%</td>
                <td>12.5% (above ₹1.25L)</td>
              </tr>
              <tr>
                <td>Debt MF (post Apr 2023)</td>
                <td>N/A</td>
                <td>Slab Rate</td>
                <td>Slab Rate</td>
              </tr>
              <tr>
                <td>Real Estate</td>
                <td>24 months</td>
                <td>Slab Rate</td>
                <td>12.5% (no indexation)</td>
              </tr>
              <tr>
                <td>Gold / Gold ETF</td>
                <td>12-24 months</td>
                <td>Slab/20%</td>
                <td>12.5%</td>
              </tr>
              <tr>
                <td>Unlisted Shares</td>
                <td>24 months</td>
                <td>Slab Rate</td>
                <td>12.5%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CapitalGainCalculator;
